import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, Typography, Box, LinearProgress, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, DollarSign, Activity, Users, Star, Calendar, Clock, MapPin, ShoppingCart, UserCheck, Heart, Target, Zap } from 'lucide-react';

const CustomerAnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');

  useEffect(() => {
    fetchCustomerStats();
  }, [selectedPeriod]);

  const fetchCustomerStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/customer_performance?days=${selectedPeriod}`);
      const data = await response.json();
      
      if (response.ok) {
        setStats(data);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch customer statistics');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = () => {
    fetchCustomerStats();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-600">
            <Activity className="h-5 w-5" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats || !stats.customers) {
    return <div>No customer data available</div>;
  }

  // Calculate summary metrics from customer data
  const totalCustomers = stats.customers.length;
  const totalSpent = stats.customers.reduce((sum, customer) => sum + (customer.total_spent || 0), 0);
  const totalRides = stats.customers.reduce((sum, customer) => sum + (customer.total_rides_count || 0), 0);
  const avgSpentPerCustomer = totalCustomers > 0 ? totalSpent / totalCustomers : 0;
  const avgCostPerRide = totalRides > 0 ? totalSpent / totalRides : 0;

  // Top performers
  const topSpenders = stats.customers
    .sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0))
    .slice(0, 5);

  const mostActiveCustomers = stats.customers
    .sort((a, b) => (b.total_rides_count || 0) - (a.total_rides_count || 0))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Analytics</h1>
          <p className="text-gray-600">Comprehensive customer performance and behavior analytics</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button
            onClick={refreshStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Total Customers
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">{totalCustomers}</div>
            <p className="text-xs text-muted-foreground">
              Active customers in period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Total Revenue
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">${totalSpent.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              Avg per customer: ${avgSpentPerCustomer.toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Total Rides
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">{totalRides}</div>
            <p className="text-xs text-muted-foreground">
              Avg cost per ride: ${avgCostPerRide.toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Avg Customer Value
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">${avgSpentPerCustomer.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              Per customer in period
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Spenders */}
        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Top Spenders
                </Typography>
              }
            />
          <CardContent>
            <div className="space-y-4">
              {topSpenders.map((customer, index) => (
                <div key={customer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{customer.name}</div>
                      <div className="text-sm text-gray-600">{customer.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">${(customer.total_spent || 0).toFixed(2)}</div>
                    <div className="text-sm text-gray-600">{customer.total_rides_count || 0} rides</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Most Active Customers */}
        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Most Active Customers
                </Typography>
              }
            />
          <CardContent>
            <div className="space-y-4">
              {mostActiveCustomers.map((customer, index) => (
                <div key={customer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{customer.name}</div>
                      <div className="text-sm text-gray-600">{customer.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-blue-600">{customer.total_rides_count || 0} rides</div>
                    <div className="text-sm text-gray-600">${(customer.total_spent || 0).toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Breakdown */}
      <Card>
        <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Customer Breakdown
                </Typography>
              }
            />
        <CardContent>
          <div className="space-y-6">
            {/* Customer Distribution */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Customer Distribution</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.customers.filter(c => (c.total_spent || 0) > 100).length}
                  </div>
                  <div className="text-sm text-blue-800">High Value ($100+)</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {stats.customers.filter(c => (c.total_spent || 0) > 50 && (c.total_spent || 0) <= 100).length}
                  </div>
                  <div className="text-sm text-green-800">Medium Value ($50-$100)</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {stats.customers.filter(c => (c.total_spent || 0) <= 50).length}
                  </div>
                  <div className="text-sm text-yellow-800">Low Value (≤$50)</div>
                </div>
              </div>
            </div>

            {/* Ride Type Distribution */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Ride Type Distribution</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {stats.customers.reduce((sum, c) => sum + (c.private_rides_count || 0), 0)}
                  </div>
                  <div className="text-sm text-purple-800">Private Rides</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {stats.customers.reduce((sum, c) => sum + (c.shared_rides_count || 0), 0)}
                  </div>
                  <div className="text-sm text-orange-800">Shared Rides</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Engagement Metrics */}
      <Card>
        <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Customer Engagement Metrics
                </Typography>
              }
            />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {stats.customers.filter(c => (c.total_rides_count || 0) > 10).length}
              </div>
              <div className="text-sm text-red-800">Very Active (10+ rides)</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {stats.customers.filter(c => (c.total_rides_count || 0) >= 5 && (c.total_rides_count || 0) <= 10).length}
              </div>
              <div className="text-sm text-blue-800">Active (5-10 rides)</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">
                {stats.customers.filter(c => (c.total_rides_count || 0) < 5).length}
              </div>
              <div className="text-sm text-gray-800">Occasional (&lt;5 rides)</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerAnalyticsDashboard;
